import { useState, useContext, useEffect } from 'react';
import { Container, Grid, Typography } from "@mui/material";
import { ImageContext } from "../../../../shared/tools/image/imageContext";
import { useLangContext } from "../../../../shared/features/Language";
import { resizeImg } from '../../../../shared/tools/image';
import { useHistory } from "react-router";
import { LinkButton } from "../../../../shared/components";
import { CustomButton } from "../../../../shared/components/Buttons/CustomButton";
import { BricksCount } from "./BricksCount";
import {getArt, IDifType, InRGB, IPaletteType} from "./Art";
import "./ArtCreateView.scss";
import { disposeCanvas } from "../../helpers";
import { createImage } from "../../../../shared/tools/image/crop-utils";
import spinner from "shared/assets/icons/spinner.svg";
import { ArtRenderGrid } from './ArtRenderGrid';

export interface IPreset {
  paletteId: IPaletteType,
  diffusion: IDifType,
  grey?: InRGB,
  matrix?: string | string[],
  alterImage?: boolean,
  L?: boolean,
  G?: boolean,
  levels?: {gamma?: number, min?: number, max?: number},
}

export const ArtCreateView = () => {
  const { textData } = useLangContext();
  const history = useHistory();

  const { croppedImage, size, setSchema, setLoadingStatus, croppedImage2 } = useContext(ImageContext);
  const [imageData, setImageData] = useState<ImageData | undefined>();
  const [imageData2, setImageData2] = useState<ImageData | undefined>();
  const [previews, setPreviews] = useState<string[]>([]);
  const paletteId: IPaletteType = 'default';
  const preset: IPreset[] = [
    { paletteId: paletteId, diffusion: 'null', matrix: 'm1' },
    { paletteId: paletteId, diffusion: 'null', matrix: ['blur','m1'], grey:'red', levels:{gamma: -.3}, G:true},
    { paletteId: paletteId, diffusion: 'null', matrix: 'm3_cross', levels:{gamma: .5}, G:true },
    { paletteId: paletteId, diffusion: 'lite', matrix: 'm1' },
    { paletteId: paletteId, diffusion: 'full', matrix: 'm1' },
    { paletteId: paletteId, diffusion: 'null', matrix: 'm3_xl', alterImage: true },
    { paletteId: paletteId, diffusion: 'lite', matrix: 'm3_cross', alterImage: true },
    { paletteId: paletteId, diffusion: 'null', matrix: 'm3_s', grey: 'average', alterImage: true },
    ];
  const [artSet, setArts] = useState(new Array(preset.length));
  const [activeArt, setActiveArt] = useState(0);

  useEffect(() => {
    setLoadingStatus(false);
    if (!(croppedImage && croppedImage2)) {
      history.push('/image-upload');
      return;
    }

    const loadAndProcessImage = (
      img: HTMLImageElement,
      callback: (imageData: ImageData) => void
    ) => {
      const canvas = document.createElement('canvas');
      const targetWidth = size.w;
      const targetHeight= size.h;

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx)
        return;

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      let imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
      imageData = resizeImg(imageData, size.w, size.h);
      callback(imageData);
      disposeCanvas(canvas)
    };

    const run = async () => {
      try {
        // Step 1: Process first and second images
        const img1 = await createImage(croppedImage);
        const img2 = await createImage(croppedImage2);

        loadAndProcessImage(img1, (data1) => {
          setImageData(data1);
          URL.revokeObjectURL(croppedImage);

          loadAndProcessImage(img2, (data2) => {
            setImageData2(data2);
            URL.revokeObjectURL(croppedImage2);
          });
        });
      } catch (error) {
        console.error("Error loading images:", error);
        history.push('/image-upload');
      }
    };
    run();

  }, [croppedImage, croppedImage2]);

  useEffect(() => {
    if (!(imageData && imageData2)) return;

    const promises = preset.map((x, i) => {
      return () => (
        new Promise((resolve) => {
          setTimeout(() => { resolve(x); }, 500);
        })
          .then(() => getArt({ imageData: x.alterImage ? imageData2 : imageData, ...x }))
          .then(z => {
            const newArray = [...artSet];
            newArray[i] = z;
            artSet[i] = z;
            setArts(newArray);
            return z;
          })
          .then(x => {
            const _p = [...previews];
            _p[i] = i.toString();
            previews[i] = i.toString();
            setPreviews(_p);
          })
      )
    });

    promises.reduce((acc, cur) => acc.then(() => cur()), Promise.resolve());

  }, [imageData, imageData2]);

  return (
    <Container
      sx={{
        height: '100%',
        maxWidth: '980px',
        marginInline: 'auto',
      }}
    >
      <div className="art-view-component">
        <Grid item>
          <Typography
            variant='body2'
            sx={{ textAlign: 'center' }}
          >{textData.artPreviewSuppose}</Typography>
        </Grid>

        <div className="art-view-wrapper">
          <Grid item>
            <BricksCount counts={artSet[activeArt]?.counts} paletteId={paletteId} />
          </Grid>
          <div className="art-view-preview">
            {previews[activeArt] ? (
              <ArtRenderGrid schema={artSet[activeArt].schema} />
            ) : (
              <img src={spinner} alt="preview" />
            )}
          </div>
          <div className="arts-scroller">
            <div className="arts-wrapper">
              {artSet.map((art, i) => (
                <div className={`art-item-preview${(activeArt === i) ? " active" : ""}`}
                  onClick={() => {
                    setActiveArt(i)
                  }} key={i}>
                  {previews[i] ? (
                    <ArtRenderGrid schema={art.schema} />
                  ) : (
                    <img src={spinner} alt="preview" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <Grid container item
          sx={{
            gap: { lg: '40px', xs: '10px' },
            justifyContent: 'center'
          }}
        >
          <Grid item>
            <LinkButton
              to={'/image-upload'}
              width={{ lg: '250px', xs: '150px' }}
              color='secondary'
            >{textData.buttonCaptionBack}</LinkButton>
          </Grid>
          <Grid item>
            <CustomButton
              width={{ lg: '250px', xs: '150px' }}
              onClick={() => {
                setSchema(artSet[activeArt]?.schema);
                history.push('/email');
              }}
            >{textData.buttonCaptionNext}</CustomButton>
          </Grid>
        </Grid>
      </div>
    </Container>
  );
}
